import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS } from '~/shared/constants';
import { getPaginationActions } from '~/shared/helpers/redux-helpers';

type CoursesSliceType = {
  lessonExerciseIndex: number;
  exercisesTab: number;
  filesManagerExercisesTab: number;
  isUpdateEditableExerciseForm: boolean;
  editableLessonExercise: null;
  toDoLessonExercisesPagination: {
    isLoading: boolean;
    items: [];
    pageInfo: initialPageInfo;
  };
  personalLessonExercisesPagination: {
    isLoading: boolean;
    items: [];
    pageInfo: initialPageInfo;
  };
  sharedLessonExercisesPagination: {
    isLoading: boolean;
    items: [];
    pageInfo: initialPageInfo;
  };
  personalFilesLessonExercisesPagination: {
    isLoading: boolean;
    items: [];
    pageInfo: initialPageInfo;
  };
  sharedFilesLessonExercisesPagination: {
    isLoading: boolean;
    items: [];
    pageInfo: initialPageInfo;
  };
};

export const pagesInitialState = {
  items: [],
  isLoading: false,
  exercisesTab: 0,
  pageInfo: {
    hasPreviousPage: false,
    hasNextPage: false,
    currentPage: 1,
    perPage: LIMIT_FOR_FETCHING_ADMIN_ITEMS,
    itemCount: 0,
    pageCount: 1,
  },
};

const initialState: CoursesSliceType = {
  lessonExerciseIndex: 0,
  filesManagerExercisesTab: 0,
  isUpdateEditableExerciseForm: false,
  editableLessonExercise: null,
  editableLessonExerciseId: null,
  personalLessonExercisesPagination: pagesInitialState,
  toDoLessonExercisesPagination: pagesInitialState,
  sharedLessonExercisesPagination: pagesInitialState,
  personalFilesLessonExercisesPagination: pagesInitialState,
  sharedFilesLessonExercisesPagination: pagesInitialState,
};

export const lessonExercisesSlice = createSlice({
  name: 'lessonExercises',
  initialState,
  reducers: {
    setLessonExerciseIndex: (state, action: PayloadAction<number>) => {
      state.lessonExerciseIndex = action.payload;
    },
    setEditableLessonExercise: (state, action: PayloadAction<null>) => {
      state.editableLessonExercise = action.payload;
    },
    setFilesManagerExercisesTab: (state, action: PayloadAction<null>) => {
      state.filesManagerExercisesTab = action.payload;
    },
    setExercisesTab: (state, action: PayloadAction<null>) => {
      state.exercisesTab = action.payload;
    },
    setEditableLessonExerciseId: (state, action: PayloadAction<null>) => {
      state.editableLessonExerciseId = action.payload;
    },
    setIsUpdateEditableExerciseForm: (state, action: PayloadAction<null>) => {
      state.isUpdateEditableExerciseForm = action.payload;
    },
    setPersonalLessonExercises: (state, action: PayloadAction<[]>) => {
      state.personalLessonExercisesPagination.items = action.payload;
    },
    setToDoLessonExercisesPaginationLoading: (state, action: PayloadAction<[]>) => {
      state.toDoLessonExercisesPagination.isLoading = action.payload;
    },
    resetExerciseFilesSelectionById: (state, action) => {
      const { exerciseId, checkedStatus } = action.payload;

      const updateExercise = (list) =>
        list?.map((exercise) =>
          exercise?._id === exerciseId
            ? {
                ...exercise,
                files: (exercise.files ?? []).map((file) => ({
                  ...file,
                  checkedStatus,
                })),
              }
            : exercise,
        );

      state.personalLessonExercisesPagination.items = updateExercise(state.personalLessonExercisesPagination.items);
      state.sharedLessonExercisesPagination.items = updateExercise(state.sharedLessonExercisesPagination.items);
    },
    appendToDoLessonExercisesPagination: (state, action: PayloadAction<[]>) => {
      state.toDoLessonExercisesPagination.items.push(...action.payload.items);
      state.toDoLessonExercisesPagination.pageInfo = action.payload.pageInfo;
    },
    setToDoLessonExercises: (state, action: PayloadAction<[]>) => {
      state.toDoLessonExercisesPagination.items = action.payload;
    },
    setSharedLessonExercises: (state, action: PayloadAction<[]>) => {
      state.sharedLessonExercisesPagination.items = action.payload;
    },

    setToDoLessonExercisesPagination: (state, action: PayloadAction<string>) => {
      state.toDoLessonExercisesPagination = action.payload;
    },
    setPersonalLessonExercisesPagination: (state, action: PayloadAction<string>) => {
      state.personalLessonExercisesPagination = action.payload;
    },
    ...getPaginationActions('personalLessonExercisesPagination'),
    setSharedLessonExercisesPagination: (state, action: PayloadAction<string>) => {
      state.sharedLessonExercisesPagination = action.payload;
    },
    ...getPaginationActions('sharedLessonExercisesPagination'),
    setPersonalFilesLessonExercisesPagination: (state, action: PayloadAction<string>) => {
      state.personalFilesLessonExercisesPagination = action.payload;
    },
    ...getPaginationActions('personalFilesLessonExercisesPagination'),
    setSharedFilesLessonExercisesPagination: (state, action: PayloadAction<string>) => {
      state.sharedFilesLessonExercisesPagination = action.payload;
    },
    ...getPaginationActions('sharedFilesLessonExercisesPagination'),
  },
});

export const {
  setSharedLessonExercisesPaginationLoading,
  appendSharedLessonExercisesPagination,
  setToDoLessonExercisesPaginationLoading,
  appendToDoLessonExercisesPagination,
  setToDoLessonExercisesPagination,
  setSharedLessonExercises,
  setIsUpdateEditableExerciseForm,
  setPersonalFilesLessonExercisesPagination,
  appendPersonalFilesLessonExercisesPagination,
  appendSharedFilesLessonExercisesPagination,
  setPersonalFilesLessonExercisesPaginationLoading,
  setSharedFilesLessonExercisesPaginationLoading,
  setSharedFilesLessonExercisesPagination,
  setEditableLessonExerciseId,
  setPersonalLessonExercisesPaginationLoading,
  appendPersonalLessonExercisesPagination,
  setFilesManagerExercisesTab,
  setEditableLessonExercise,
  resetExerciseFilesSelectionById,
  setPersonalLessonExercisesPagination,
  setExercisesTab,
  setSharedLessonExercisesPagination,
  setToDoLessonExercises,
  setPersonalLessonExercises,
  setLessonExerciseIndex,
} = lessonExercisesSlice.actions;
